import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { default as React, useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaFilter, FaIdCard, FaPalette, FaSearch, FaSitemap } from "react-icons/fa";
import { FaUserLarge, FaUsersGear } from "react-icons/fa6";
import { FiFilter } from "react-icons/fi";
import { IoChevronDownSharp, IoFilterOutline } from "react-icons/io5";
import { RiAdminLine } from "react-icons/ri";
import { RxCross2 } from "react-icons/rx";
import { AccessTypes, LabelConstants } from "../../Constants.ts";
import { useAppContext } from "../../context/AppContext.tsx";
import { ThemeContext } from "../../context/ThemeContext.js";
import DateRangeSelector from "../../UIComponents/DateRangeSelector.tsx";
import MultiSelectDropdown from "../../UIComponents/MultiSelectDropdownProps.tsx";
import { AreObjectsEquals, getUserName } from "../../Utilities/Utilities.ts";
import TooltipButton from "../Tools/TooltipButton.tsx";
import SingleSelectDropdown from "../../UIComponents/SingleSelectDropdown.tsx";


export enum appliedFilterType {
    None, Name, Role, RoleGroup, User, Dimensions, UserAccess, Date, Dropdown, UserADStatus, SingleDropdown
}

export const UserADStatus = {
    Imported: 0,
    NotInAd: -1,
    InAD: 1
}

interface filters {
    appliedFilter: appliedFilterType, name: string, roleId: string | null, dataMapColumn: string | null,
    roleGroupID: string | null, userID: string | null, appliationAccess: string | null
}

interface keyValue {
    key: string,
    value: string
}

interface eachFilter {
    filterType: appliedFilterType,
    label: string,
    icon: React.JSX.Element,
    exValues: keyValue[],
    appliedFilter?: any
}

interface request {
    filters: { [key: string]: eachFilter },
    values: { [key: string]: string | null },
    initialValues: { [key: string]: string | null },
    exButton: any,
    onChange: (filters: { [key: string]: string | null }, afterLoad: (val: any) => void) => void,
}

const typeIcons = {
    [appliedFilterType.Dimensions]: <FaPalette />,
    [appliedFilterType.RoleGroup]: <FaSitemap />,
    [appliedFilterType.Role]: <FaUsersGear />,
    [appliedFilterType.User]: <FaUserLarge />,
    [appliedFilterType.UserAccess]: <RiAdminLine />,
    [appliedFilterType.UserADStatus]: <FaIdCard />
}

export const typeNames = {
    [appliedFilterType.Dimensions]: "Dimensions",
    [appliedFilterType.RoleGroup]: [LabelConstants.RoleGroups],
    [appliedFilterType.Role]: "Roles",
    [appliedFilterType.User]: "Users",
    [appliedFilterType.UserAccess]: "Application Access",
    [appliedFilterType.UserADStatus]: "AD Status"
}

export function SearchAndAdvancedFilters({ filters, values, initialValues, exButton, onChange }: request) {
    const { dimensions, refreshRoles, allUsers, allRoleGroups, roles } = useAppContext();
    const { themeConfig } = useContext(ThemeContext);
    const [applyingField, setApplyingField] = useState<string>("");
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const defaultFilters = JSON.parse(JSON.stringify({ ...values, name: "" }));
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string | null }>({});
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let iniVal = JSON.parse(JSON.stringify(defaultFilters));
        if (typeof initialValues != 'undefined') {
            for (var key in initialValues)
                if (initialValues[key] != null)
                    iniVal[key] = initialValues[key];
        }
        setSelectedFilters(iniVal);
    }, [])

    const updateFilter = (changing: string, f: { [key: string]: string | null }) => {
        setApplyingField(changing);
        setSelectedFilters({ ...f });
        let nVal = JSON.parse(JSON.stringify(f));
        if (nVal.dataMapColumn != null) {
            let newCode = "";
            nVal.dataMapColumn.split(",").map((c: any) => {
                if (newCode != "")
                    newCode += ",";
                newCode += "'" + c + "'";
            });
            nVal.dataMapColumn = newCode;
        }
        onChange(nVal, (v) => {
            let prev = f;
            setApplyingField("");
            setSelectedFilters({ ...prev });
        })
    }

    const getOptions = (filter: eachFilter) => {
        let vals: {
            key: any,
            value: string
        }[] = [];
        let t = filter.filterType;
        switch (t) {
            case appliedFilterType.Dimensions:
                vals = dimensions.map((x) => {
                    return ({ key: x.dimensionCode, value: x.dimensionName })
                });
                break;
            case appliedFilterType.Role:
                vals = roles.map((x) => {
                    return ({ key: x.roleID == null ? 0 : x.roleID, value: x.roleName })
                });
                break;
            case appliedFilterType.RoleGroup:
                vals = allRoleGroups.map((x) => {
                    return ({ key: x.roleGroupID, value: x.roleGroupName })
                });
                break;
            case appliedFilterType.User:
                if (typeof filter.appliedFilter != 'undefined' && filter.appliedFilter != null) {
                    vals = allUsers.filter(filter.appliedFilter).map((x) => {
                        return ({ key: x.userID, value: getUserName(x) })
                    });
                }
                else
                    vals = allUsers.map((x) => {
                        return ({ key: x.userID, value: getUserName(x) })
                    });
                break;
            case appliedFilterType.UserAccess:
                vals = AccessTypes.map((x) => {
                    return ({ key: x.id, value: x.value })
                });
                break;
            case appliedFilterType.UserADStatus:
                vals = [
                    { key: UserADStatus.InAD, value: "Active AD User" },
                    { key: UserADStatus.NotInAd, value: "Unlinked AD User" },
                    // { key: UserADStatus.Imported, value: "Local User" }
                ];
                break;
            case appliedFilterType.Dropdown:
                vals = filter.exValues;
                break;
            case appliedFilterType.SingleDropdown:
                vals = filter.exValues;
                break;
        }
        return vals;
    }

    const getFieldName = (t: appliedFilterType) => {
        let vals = "";
        switch (t) {
            case appliedFilterType.Dimensions:
                vals = "dataMapColumn"
                break;
            case appliedFilterType.Role:
                vals = "roleId"
                break;
            case appliedFilterType.RoleGroup:
                vals = "roleGroupID"
                break;
            case appliedFilterType.User:
                vals = "userID"
                break;
            case appliedFilterType.UserAccess:
                vals = "appliationAccess"
                break;
            case appliedFilterType.UserADStatus:
                vals = "isAdUser"
                break;
        }
        return vals;
    }

    const getValuesForField = (fieldName: string, filter: eachFilter) => {
        let val: string[] = [];
        let arr: any = [];
        let t = filter.filterType;
        switch (t) {
            case appliedFilterType.Dimensions:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = dimensions.filter((x) => arr.indexOf(x.dimensionCode) > -1);
                    val = d.map((x) => x.dimensionName);
                }
                break;
            case appliedFilterType.Role:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = roles.filter((x) => arr.indexOf(x.roleID?.toString()) > -1);
                    val = d.map((x) => x.roleName);
                }
                break;
            case appliedFilterType.RoleGroup:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = allRoleGroups.filter((x) => arr.indexOf(x.roleGroupID.toString()) > -1);
                    val = d.map((x) => x.roleGroupName);
                }
                break;
            case appliedFilterType.User:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = allUsers.filter((x) => arr.indexOf(x.userID.toString()) > -1);
                    val = d.map((x) => getUserName(x));
                }
                break;
            case appliedFilterType.UserAccess:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = AccessTypes.filter((x) => arr.indexOf(x.id.toString()) > -1);
                    val = d.map((x) => x.value);
                }
                break;
            case appliedFilterType.UserADStatus:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    const statusMap: Record<number, string> = {
                        [UserADStatus.InAD]: "Active AD User",
                        [UserADStatus.NotInAd]: "Unlinked AD User",
                        // [UserADStatus.Imported]: "Local User"
                    };
                    val = arr.map((x: string) => statusMap[parseInt(x)]).filter(Boolean);
                }
                break;
            case appliedFilterType.Dropdown:
                arr = selectedFilters[fieldName]?.split(",");
                if (typeof arr != 'undefined') {
                    let d = filter.exValues.filter((x) => arr.indexOf(x.key) > -1);
                    val = d.map((x) => x.value);
                }
                break;
            case appliedFilterType.SingleDropdown:
                arr = selectedFilters[fieldName];
                if (typeof arr != 'undefined' && arr != null) {
                    let d = filter.exValues.filter((x) => x.key.toString() === arr);
                    val = d.map((x) => x.value);
                }
                break;
        }
        return val;
    }

    const getFirstNameAndCount = (fieldName: string, t: eachFilter) => {
        let names = getValuesForField(fieldName, t);
        if (names.length > 0) {
            let val = names[0];
            if (names.length > 1)
                val += " +(" + (names.length - 1) + ")";
            return val;
        }
        return "";
    }

    const getSelectedString = (fld: string) => {
        let val = "";
        if (selectedFilters[fld] != null)
            val = selectedFilters[fld];
        return val;
    }

    return (
        <div className='d-flex flex-column gap-0 w-100'>
            <div className='gap-2' style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <AnimatePresence>
                    <div key="search-bar" className="input-group j_srch">
                        <span className="input-group-text text-muted-theme bg-theme-card">
                            <FaSearch />
                        </span>
                        <input
                            type="text"
                            className="form-control search-control-with-button"
                            placeholder="Search..."
                            value={(selectedFilters["name"] == null) ? "" : selectedFilters["name"]}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const updatedFilters = { ...selectedFilters, name: newValue };

                                setSelectedFilters(updatedFilters);
                                if (typingTimeoutRef.current) {
                                    clearTimeout(typingTimeoutRef.current);
                                }

                                typingTimeoutRef.current = setTimeout(() => {
                                    updateFilter("name", updatedFilters);
                                }, 400);
                            }}
                        />
                        <TooltipButton title={AreObjectsEquals(defaultFilters, selectedFilters) ?
                            (!showAdvanced ? "Click to view advanced filter" : "Click to hide advanced filter options") :
                            (!showAdvanced ? "Click to view/change applied filter(s)" : "Click to hide advanced filter options")}>
                            <button className='btn btn-primary py-2 j_fil' onClick={() => {
                                setShowAdvanced(!showAdvanced);
                            }}>

                                <div className='d-flex flex-row align-items-center gap-1'>
                                    {
                                        AreObjectsEquals(defaultFilters, selectedFilters) ?
                                            <FiFilter /> :
                                            <FaFilter />
                                    }
                                    {
                                        showAdvanced ?
                                            "Hide filter" : "Show filter"
                                    }
                                </div>

                            </button>
                        </TooltipButton>
                    </div>
                    {
                        typeof exButton != 'undefined' &&
                        exButton
                    }
                </AnimatePresence>
            </div >
            <AnimatePresence>
                {
                    showAdvanced &&
                    <motion.div
                        className='d-flex flex-row align-items-center justify-content-between px-3 py-2 mt-1 rounded-2 shadow-sm'
                        style={{
                            backgroundColor: themeConfig?.colors?.card || 'var(--bs-tertiary-bg)',
                            color: themeConfig?.colors?.text || 'inherit',
                            border: `1px solid ${themeConfig?.colors?.border || 'var(--bs-border-color)'}`
                        }}
                        key="advanced-filter"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}>
                        <div className='d-flex flex-row align-items-center gap-2'>
                            <span className='d-flex flex-row align-items-center gap-1 fw-medium'>
                                <IoFilterOutline />Filter:
                            </span>
                            {
                                Object.keys(filters).map((key, indx) => (
                                    filters[key].filterType == appliedFilterType.Date ?
                                        <DateRangeSelector
                                            key={`date-filter-${indx}`}
                                            initialStartDate={selectedFilters[key] != null ? new Date(selectedFilters[key].split(",")[0]) : null}
                                            initialEndDate={selectedFilters[key] != null ? new Date(selectedFilters[key].split(",")[1]) : null}
                                            label={filters[key].label}
                                            clearTrigger={selectedFilters[key] == null ? Math.random() : undefined}
                                            onChange={(start, end) => {
                                                let prev = selectedFilters;
                                                if (start == null || start == null)
                                                    prev[key] = null;
                                                else {
                                                    prev[key] = moment(start).format("yyyy-MM-DD") + "," + moment(end).format("yyyy-MM-DD");
                                                }
                                                updateFilter(key, { ...prev });
                                            }}
                                        />
                                        :
                                        filters[key].filterType == appliedFilterType.SingleDropdown ?
                                            <SingleSelectDropdown
                                                key={`dropdown-filter-${indx}`}
                                                label={undefined}
                                                maxWidth={undefined}
                                                icon={filters[key].icon}
                                                options={getOptions(filters[key])}
                                                selected={getSelectedString(key) || null}
                                                onChange={(v) => {
                                                    let prev = selectedFilters;
                                                    prev[key] = v ? v.toString() : null; // Save as string to match your existing state map
                                                    updateFilter(key, { ...prev });
                                                }}
                                                initiator={(clickCall) => {
                                                    return (
                                                        <button
                                                            className="btn d-flex align-items-center gap-2 py-2 px-3"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                color: 'inherit',
                                                                border: `1px solid ${themeConfig?.colors?.border || 'var(--bs-border-color)'}`
                                                            }}
                                                            onClick={clickCall}
                                                        >
                                                            <span className='d-flex flex-row align-items-center gap-2'>
                                                                <TooltipButton
                                                                    title={<>{filters[key].label}{(selectedFilters[key] == null) ? "" : ": "}{getValuesForField(key, filters[key]).join(",")}</>}>
                                                                    <span className='d-flex flex-row align-items-center gap-1'>
                                                                        {filters[key].icon}
                                                                        <span className='d-flex flex-row align-items-center gap-1'>
                                                                            {selectedFilters[key] == null && <span className="fw-normal">{filters[key].label}</span>}
                                                                            {applyingField == key ?
                                                                                <Spinner animation="border" size='sm' /> :
                                                                                <span className='d-inline-block fw-bold'>
                                                                                    {getFirstNameAndCount(key, filters[key])}</span>
                                                                            }
                                                                        </span>
                                                                    </span>
                                                                </TooltipButton>
                                                                <IoChevronDownSharp size={20} />
                                                            </span>
                                                        </button>
                                                    )
                                                }}
                                            /> :
                                            <MultiSelectDropdown
                                                key={`dropdown-filter-${indx}`}
                                                label={undefined}
                                                maxWidth={undefined}
                                                icon={filters[key].icon}
                                                options={getOptions(filters[key])}
                                                selected={selectedFilters[key] == null ? [] : selectedFilters[key].split(",").map(x => (isNaN(parseInt(x))) ? x : parseInt(x))}
                                                onChange={(v) => {
                                                    let prev = selectedFilters;
                                                    prev[key] = v.join(",");
                                                    if (prev[key] == "")
                                                        prev[key] = null;
                                                    updateFilter(key, { ...prev });
                                                }}
                                                initiator={(clickCall) => {
                                                    return (
                                                        <button
                                                            className="btn d-flex align-items-center gap-2 py-2 px-3"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                color: 'inherit',
                                                                border: `1px solid ${themeConfig?.colors?.border || 'var(--bs-border-color)'}`
                                                            }}
                                                            onClick={clickCall}
                                                        >
                                                            <span className='d-flex flex-row align-items-center gap-2'>
                                                                <TooltipButton
                                                                    title={<>{filters[key].label}{(selectedFilters[key] == null) ? "" : ": "}{getValuesForField(key, filters[key]).map((x, i) => (i > 0) ? " " + x : x).join(",")}</>}>
                                                                    <span className='d-flex flex-row align-items-center gap-1'>
                                                                        {filters[key].icon}
                                                                        <span className='d-flex flex-row align-items-center gap-1'>
                                                                            {selectedFilters[key] == null && <span className="fw-normal">{filters[key].label}</span>}
                                                                            {applyingField == key ?
                                                                                <Spinner animation="border" size='sm' /> :
                                                                                <span className='d-inline-block fw-bold'>
                                                                                    {getFirstNameAndCount(key, filters[key])}</span>
                                                                            }
                                                                        </span>
                                                                    </span>
                                                                </TooltipButton>
                                                                <IoChevronDownSharp size={20} />
                                                            </span>
                                                        </button>)
                                                }}
                                            />
                                ))
                            }
                        </div>
                        {
                            !AreObjectsEquals(defaultFilters, selectedFilters) &&
                            <TooltipButton title="Click to clear all applied filters">
                                <button
                                    className="btn d-flex align-items-center justify-content-center"
                                    style={{ color: 'inherit' }}
                                    onClick={() => {
                                        updateFilter("", defaultFilters);
                                    }}
                                >
                                    <RxCross2 size={18} />
                                </button>
                            </TooltipButton>
                        }
                    </motion.div>
                }
            </AnimatePresence>
        </div >
    )
}

export default SearchAndAdvancedFilters;